import React, { useEffect, useState } from "react";
import {
	TextField,
	Button,
	Typography,
	Switch,
	FormControlLabel,
	Alert,
	Box,
	Modal,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import supabase from "../../supabaseClient";

const LoginSignup = ({ doRedirect }) => {
	const navigate = useNavigate();
	const [isSignUp, setIsSignUp] = useState(false);
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		homeZip: "",
		profileVisibility: "private",
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [forgotModalOpen, setForgotModalOpen] = useState(false);
	const [forgotEmail, setForgotEmail] = useState("");
	const [forgotStatus, setForgotStatus] = useState("");

	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleCloseModal = () => {
		setForgotModalOpen(false);
		setForgotEmail("");
		setForgotStatus("");
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		setSuccess("");

		if (isSignUp) {
			// Sign-Up Flow

			const { data, error } = await supabase.auth.signUp({
				email: formData.email,
				password: formData.password,
			});

			if (error) {
				setError(error.message);
				setLoading(false);
				return;
			}
			//set this within the function, since it wont render properly if we set it outside
			//and then use it right away
			const supabaseId = data?.user?.id;

			await supabase.auth.signOut();
			// Add user to profiles table
			const { error: profileError } = await supabase.from("profiles").insert([
				{
					supabaseId: supabaseId,
					first_name: formData.firstName,
					last_name: formData.lastName,
					email: formData.email,
					home_zip: formData.homeZip,
					profile_visibility: formData.profileVisibility,
				},
			]);

			if (profileError) {
				setError(profileError.message);
				setLoading(false);
				return;
			}

			try {
				await axios.post(
					//get the supabase url from the environment variable
					//and add the function name to the end
					`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-verification-email`,
					{ email: formData.email }, // Body should just be the object
					{
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
						},
					},
				);
			} catch (error) {
				console.error("Error sending verification email:", error);
				setError("Error sending verification email.");
				setLoading(false);
				return;
			}

			setSuccess("Account created! Verify your email to proceed.");
			setTimeout(() => {
				window.location.reload();
			}, 10000);
		} else {
			// Login Flow
			const { error } = await supabase.auth.signInWithPassword({
				email: formData.email,
				password: formData.password,
			});

			if (error) {
				setError(error.message);
				setLoading(false);
				return;
			}

			// Check if email is verified
			const { data: userProfile, error: profileError } = await supabase
				.from("profiles")
				.select("email_verified")
				.eq("email", formData.email)
				.single();

			if (profileError) {
				setError(profileError.message);
				setLoading(false);
				return;
			}

			if (!userProfile?.email_verified) {
				setError("Please verify your email before logging in.");
				setLoading(false);
				//log the user out after account creation
				await supabase.auth.signOut();
				return;
			}

			if (doRedirect) {
				setSuccess("Login successful! Redirecting...");
				setTimeout(() => navigate("/dashboard"), 1000);
			} else {
				setSuccess("Login successful!");
				setTimeout(() => window.location.reload(), 1000);
			}
		}

		setLoading(false);
	};

	useEffect(() => {
		// Check if the user is already signed in
		const checkUser = async () => {
			if (!doRedirect) return;
			const { data } = await supabase.auth.getSession();
			if (data.session) {
				navigate("/dashboard");
			}
		};
		checkUser();
	}, [doRedirect, navigate]);

	return (
		<Box padding={2}>
			<Typography variant="h4" textAlign="center">
				{isSignUp ? "Sign Up" : "Login"}
			</Typography>
			{error && <Alert severity="error">{error}</Alert>}
			{success && <Alert severity="success">{success}</Alert>}
			<form onSubmit={handleSubmit} width={"100%"}>
				{isSignUp && (
					<>
						<TextField
							label="First Name"
							name="firstName"
							fullWidth
							margin="normal"
							onChange={handleChange}
							required
						/>
						<TextField
							label="Last Name"
							name="lastName"
							fullWidth
							margin="normal"
							onChange={handleChange}
							required
						/>
						<TextField
							label="Zip Code"
							name="homeZip"
							fullWidth
							type="number"
							margin="normal"
							onChange={handleChange}
							required
						/>
						<FormControlLabel
							control={
								<Switch
									checked={formData.profileVisibility === "public"}
									onChange={() =>
										setFormData({
											...formData,
											profileVisibility:
												formData.profileVisibility === "private"
													? "public"
													: "private",
										})
									}
								/>
							}
							label={
								formData.profileVisibility === "private"
									? "Private Profile"
									: "Public Profile"
							}
						/>
					</>
				)}
				<TextField
					label="Email"
					name="email"
					type="email"
					fullWidth
					margin="normal"
					onChange={handleChange}
					required
				/>
				<TextField
					label="Password"
					name="password"
					type="password"
					fullWidth
					margin="normal"
					onChange={handleChange}
					required
				/>
				<Button type="submit" variant="contained" fullWidth disabled={loading}>
					{isSignUp ? "Sign Up" : "Login"}
				</Button>
			</form>
			<Button onClick={() => setIsSignUp(!isSignUp)} fullWidth>
				{isSignUp
					? "Already have an account? Login"
					: "Don't have an account? Sign up"}
			</Button>
			{!isSignUp && (
				<Box>
					<Button onClick={() => setForgotModalOpen(true)} fullWidth>
						Forgot Password?
					</Button>

					<Modal open={forgotModalOpen} onClose={() => handleCloseModal()}>
						<Box
							sx={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								bgcolor: "background.paper",
								boxShadow: 24,
								p: 4,
								borderRadius: 2,
								minWidth: 300,
							}}
						>
							<Typography variant="h6" mb={2}>
								Reset Password
							</Typography>
							<TextField
								label="Email"
								type="email"
								value={forgotEmail}
								onChange={(e) => setForgotEmail(e.target.value)}
								fullWidth
								required
								sx={{ mb: 2 }}
							/>
							<Button
								variant="contained"
								fullWidth
								onClick={async () => {
									setForgotStatus("");
									const { error } = await supabase.auth.resetPasswordForEmail(
										forgotEmail,
										{
											redirectTo:
												"https://www.gosellgardens.com/reset-password",
										},
									);
									if (error) {
										setForgotStatus(error.message);
									} else {
										setForgotStatus(
											"If an account exists, a reset email has been sent.",
										);
									}
								}}
							>
								Send Reset Email
							</Button>
							{forgotStatus && (
								<Typography
									mt={2}
									color={
										forgotStatus.startsWith("If") ? "success.main" : "error"
									}
								>
									{forgotStatus}
								</Typography>
							)}
						</Box>
					</Modal>
				</Box>
			)}
		</Box>
	);
};

export default LoginSignup;
